import { c as _c } from "react/compiler-runtime";
import { Badge, Box, Button, type ButtonProps, Heading, HStack, Progress, Separator, Stack, Text } from '@chakra-ui/react';
import type { ScoProduct } from '@src/graphql-types';
import type { ReactNode } from 'react';
import { PaywallFeatureList } from './PaywallFeatureList';
import { PlanChoiceButton } from './PlanChoiceButton';
import { resolvePaywallIntent, type PaywallIntentId } from './intent';
import { genericCtaLabel, productCanContinue, productCtaLabel, productLabel, productPaywallIntent, productPriceLabel, productPromotion } from './presentation';
export type PaywallGateState = 'locked' | 'guest' | 'entitled';
export type PaywallGateDensity = 'button' | 'inline' | 'meter' | 'boxed' | 'page' | 'overlay' | 'blank';

/** Caller-verified usage for the `meter` density (e.g. the server's Wil.ai free-message count). */
export type PaywallGateMeter = {
  used: number;
  limit: number;
  /** Plural noun for the metered thing, e.g. "free messages". */
  unit: string;
  /** When the allowance resets, read after "left", e.g. "today". */
  period?: string;
};

/** A gate is a moment, not a catalog: the few lines tied to the blocked job, then a count. */
const GATE_FEATURE_LIMIT = 4;
export type PaywallGateProps = {
  product?: ScoProduct;
  intent?: PaywallIntentId;
  tokens?: Record<string, string>;
  state?: PaywallGateState;
  density?: PaywallGateDensity;
  children?: ReactNode;
  /** Caller-verified reassurance under the paid CTA, such as “Cancel anytime.” Never inferred here. */
  assurance?: string;
  onContinue?: (product: ScoProduct) => void;
  onJoin?: (product?: ScoProduct) => void;
  onLogin?: () => void;
  onDismiss?: () => void;
  /** Catalog or viewer still loading: the CTA stays disabled and the wall stays up (fails closed). */
  loading?: boolean;
  /** Required for `meter`; ignored by other densities. */
  meter?: PaywallGateMeter;
  /** Merged onto the primary CTA so a gate can match the button it replaces (size, variant). */
  ctaProps?: ButtonProps;
  /** One-line CTA (job label only). Plan and price stay in the accessible name. */
  compactCta?: boolean;
  /** Call-site CTA copy ("Upgrade to apply"); wins over the intent's CTA. */
  ctaLabel?: string;
  /** Call-site headline when the job is sharper than the intent's copy. */
  headline?: string;
};
export const PaywallGate = t0 => {
  const $ = _c(156);
  const {
    assurance,
    children,
    compactCta: t1,
    ctaLabel,
    ctaProps,
    density: t2,
    headline,
    intent,
    loading: t3,
    meter,
    onContinue,
    onDismiss,
    onJoin,
    onLogin,
    product,
    state: t4,
    tokens
  } = t0;
  const compactCta = t1 === undefined ? false : t1;
  const density = t2 === undefined ? "boxed" : t2;
  const loading = t3 === undefined ? false : t3;
  const state = t4 === undefined ? "locked" : t4;
  if (state === "entitled") {
    let t5;
    if ($[0] !== children) {
      t5 = <>{children}</>;
      $[0] = children;
      $[1] = t5;
    } else {
      t5 = $[1];
    }
    return t5;
  }
  if (density === "blank") {
    return null;
  }
  const guest = state === "guest";
  let name;
  let price;
  let priced;
  let promotion;
  let resolved;
  let t5;
  if ($[2] !== ctaLabel || $[3] !== intent || $[4] !== loading || $[5] !== product || $[6] !== tokens) {
    resolved = product ? productPaywallIntent(product, intent, tokens) : loading ? resolvePaywallIntent(intent, tokens) : {
      id: "default" as const,
      headline: "Create your free account",
      supporting: "Join to continue where you left off.",
      cta: undefined,
      recommendedProductId: ""
    };
    let t6;
    if ($[13] !== product) {
      t6 = product ? productLabel(product) : null;
      $[13] = product;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    name = t6;
    let t7;
    if ($[15] !== product) {
      t7 = product ? productPriceLabel(product) : null;
      $[15] = product;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    price = t7;
    let t8;
    if ($[17] !== loading || $[18] !== product) {
      t8 = !loading && Boolean(product && productCanContinue(product));
      $[17] = loading;
      $[18] = product;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    priced = t8;
    let t9;
    if ($[20] !== product) {
      t9 = product ? productPromotion(product) : null;
      $[20] = product;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    promotion = t9;
    t5 = loading ? "Loading\u2026" : product ? productCtaLabel(product, resolved, ctaLabel) : ctaLabel || "Offer unavailable";
    $[2] = ctaLabel;
    $[3] = intent;
    $[4] = loading;
    $[5] = product;
    $[6] = tokens;
    $[7] = name;
    $[8] = price;
    $[9] = priced;
    $[10] = promotion;
    $[11] = resolved;
    $[12] = t5;
  } else {
    name = $[7];
    price = $[8];
    priced = $[9];
    promotion = $[10];
    resolved = $[11];
    t5 = $[12];
  }
  const jobLabel = t5;
  let t6;
  if ($[22] !== headline || $[23] !== resolved) {
    t6 = headline?.trim() || resolved.headline;
    $[22] = headline;
    $[23] = resolved;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const headlineText = t6;
  let t7;
  if ($[25] !== jobLabel || $[26] !== product) {
    t7 = product ? jobLabel === genericCtaLabel(product) : false;
    $[25] = jobLabel;
    $[26] = product;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const genericLabel = t7;
  const continueAriaLabel = loading ? jobLabel : product ? genericLabel ? `${jobLabel}, ${price}` : `${jobLabel}, ${name}, ${price}` : jobLabel;
  const showFeatures = Boolean(product) && (density === "page" || density === "overlay");
  let t8;
  if ($[28] !== ctaProps) {
    t8 = event => {
      ctaProps?.onClick?.(event);
    };
    $[28] = ctaProps;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const continueFromCta = t8;
  let t9;
  if ($[30] !== guest || $[31] !== onContinue || $[32] !== onJoin || $[33] !== product) {
    t9 = guest ? onJoin ? () => onJoin(product) : undefined : product && onContinue ? () => onContinue(product) : undefined;
    $[30] = guest;
    $[31] = onContinue;
    $[32] = onJoin;
    $[33] = product;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const choosePlan = t9;
  const guestNextStep = guest && product && priced ? `Start with a free account, then unlock this with ${name} (${price}).` : null;
  let t10;
  if ($[35] !== assurance || $[36] !== guest || $[37] !== priced) {
    t10 = !guest && priced ? assurance?.trim() || null : null;
    $[35] = assurance;
    $[36] = guest;
    $[37] = priced;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  const assuranceLine = t10;
  const t11 = guest ? undefined : continueAriaLabel;
  const t12 = guest ? loading : !priced;
  const t13 = compactCta ? undefined : "auto";
  const t14 = priced ? product : undefined;
  const t15 = compactCta ? undefined : 3;
  const t16 = compactCta ? "nowrap" : "normal";
  let t17;
  if ($[39] !== compactCta || $[40] !== ctaLabel || $[41] !== genericLabel || $[42] !== guest || $[43] !== jobLabel || $[44] !== loading || $[45] !== name || $[46] !== price || $[47] !== product) {
    t17 = guest ? compactCta ? ctaLabel || "Join to unlock" : "Create a free account" : product && !loading && !compactCta ? <Stack align="center" gap={0}><Text>{jobLabel}</Text><Text fontWeight="normal" textStyle="xs">{genericLabel ? price : `${name} · ${price}`}</Text></Stack> : jobLabel;
    $[39] = compactCta;
    $[40] = ctaLabel;
    $[41] = genericLabel;
    $[42] = guest;
    $[43] = jobLabel;
    $[44] = loading;
    $[45] = name;
    $[46] = price;
    $[47] = product;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== choosePlan || $[50] !== continueFromCta || $[51] !== ctaProps || $[52] !== t11 || $[53] !== t12 || $[54] !== t13 || $[55] !== t14 || $[56] !== t15 || $[57] !== t16 || $[58] !== t17) {
    t18 = <PlanChoiceButton aria-label={t11} disabled={t12} h={t13} product={t14} py={t15} textAlign="center" variant="cta" whiteSpace={t16} width="full" {...ctaProps} onChoose={choosePlan} onClick={continueFromCta}>{t17}</PlanChoiceButton>;
    $[49] = choosePlan;
    $[50] = continueFromCta;
    $[51] = ctaProps;
    $[52] = t11;
    $[53] = t12;
    $[54] = t13;
    $[55] = t14;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  const primaryCta = t18;
  let t19;
  if ($[60] !== assuranceLine) {
    t19 = assuranceLine ? <Text color="fg.muted" textAlign="center" textStyle="xs">{assuranceLine}</Text> : null;
    $[60] = assuranceLine;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== guest || $[63] !== onLogin) {
    t20 = guest ? <HStack gap={1} justify="center" wrap="wrap"><Text color="fg.muted" textStyle="sm">Already a member?</Text><Button h="auto" minW={0} px={1} py={1} size="sm" variant="plain" onClick={() => onLogin?.()}>Log in</Button></HStack> : null;
    $[62] = guest;
    $[63] = onLogin;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  let t21;
  if ($[65] !== density || $[66] !== onDismiss) {
    t21 = density === "overlay" && onDismiss ? <Button size="sm" variant="ghost" onClick={onDismiss}>Not now</Button> : null;
    $[65] = density;
    $[66] = onDismiss;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  let t22;
  if ($[68] !== primaryCta || $[69] !== t19 || $[70] !== t20 || $[71] !== t21) {
    t22 = <Stack gap={2}>{primaryCta}{t19}{t20}{t21}</Stack>;
    $[68] = primaryCta;
    $[69] = t19;
    $[70] = t20;
    $[71] = t21;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  const actions = t22;
  if (density === "button") {
    return compactCta ? primaryCta : actions;
  }
  if (density === "inline") {
    const t23 = guest ? "Create a free account to continue." : headlineText;
    let t24;
    if ($[73] !== t23) {
      t24 = <Text color="fg.muted" textStyle="sm">{t23}</Text>;
      $[73] = t23;
      $[74] = t24;
    } else {
      t24 = $[74];
    }
    let t25;
    if ($[75] !== choosePlan || $[76] !== continueAriaLabel || $[77] !== continueFromCta || $[78] !== ctaProps || $[79] !== guest || $[80] !== jobLabel || $[81] !== loading || $[82] !== onJoin || $[83] !== priced || $[84] !== product) {
      t25 = <Box flexShrink={0}>{guest ? <PlanChoiceButton disabled={loading} product={priced ? product : undefined} size="sm" variant="outline" onChoose={onJoin ? () => onJoin(product) : undefined}>Create a free account</PlanChoiceButton> : <PlanChoiceButton aria-label={continueAriaLabel} disabled={!priced} product={priced ? product : undefined} size="sm" variant="outline" {...ctaProps} onChoose={choosePlan} onClick={continueFromCta}>{jobLabel}</PlanChoiceButton>}</Box>;
      $[75] = choosePlan;
      $[76] = continueAriaLabel;
      $[77] = continueFromCta;
      $[78] = ctaProps;
      $[79] = guest;
      $[80] = jobLabel;
      $[81] = loading;
      $[82] = onJoin;
      $[83] = priced;
      $[84] = product;
      $[85] = t25;
    } else {
      t25 = $[85];
    }
    let t26;
    if ($[86] !== t24 || $[87] !== t25) {
      t26 = <HStack gap={2} justify="space-between" w="full" wrap="wrap">{t24}{t25}</HStack>;
      $[86] = t24;
      $[87] = t25;
      $[88] = t26;
    } else {
      t26 = $[88];
    }
    return t26;
  }
  if (density === "meter" && meter) {
    const remaining = Math.max(0, meter.limit - meter.used);
    if (remaining > 0) {
      const low = remaining <= Math.max(1, Math.ceil(meter.limit * 0.2));
      const t23 = `${remaining} of ${meter.limit}`;
      let t24;
      if ($[89] !== t23) {
        t24 = <Text as="span" fontVariantNumeric="tabular-nums" fontWeight="semibold">{t23}</Text>;
        $[89] = t23;
        $[90] = t24;
      } else {
        t24 = $[90];
      }
      const t25 = ` ${meter.unit} left${meter.period ? ` ${meter.period}` : ""}`;
      let t26;
      if ($[91] !== t24 || $[92] !== t25) {
        t26 = <Text textStyle="sm">{t24}{t25}</Text>;
        $[91] = t24;
        $[92] = t25;
        $[93] = t26;
      } else {
        t26 = $[93];
      }
      const t27 = `${meter.unit} used`;
      const t28 = low ? "orange" : "gray";
      const t29 = meter.limit;
      const t30 = Math.min(meter.used, meter.limit);
      let t31;
      if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
        t31 = <Progress.Track><Progress.Range /></Progress.Track>;
        $[94] = t31;
      } else {
        t31 = $[94];
      }
      let t32;
      if ($[95] !== meter.limit || $[96] !== t27 || $[97] !== t28 || $[98] !== t30) {
        t32 = <Progress.Root aria-label={t27} colorPalette={t28} max={t29} maxW="12rem" size="xs" value={t30}>{t31}</Progress.Root>;
        $[95] = meter.limit;
        $[96] = t27;
        $[97] = t28;
        $[98] = t30;
        $[99] = t32;
      } else {
        t32 = $[99];
      }
      let t33;
      if ($[100] !== t26 || $[101] !== t32) {
        t33 = <Stack flex="1 1 10rem" gap={1.5} minW={0}>{t26}{t32}</Stack>;
        $[100] = t26;
        $[101] = t32;
        $[102] = t33;
      } else {
        t33 = $[102];
      }
      let t34;
      if ($[103] !== choosePlan || $[104] !== continueAriaLabel || $[105] !== continueFromCta || $[106] !== ctaProps || $[107] !== guest || $[108] !== jobLabel || $[109] !== low || $[110] !== priced || $[111] !== product) {
        t34 = guest ? null : <PlanChoiceButton aria-label={continueAriaLabel} disabled={!priced} product={priced ? product : undefined} size="sm" variant={low ? "cta" : "outline"} {...ctaProps} onChoose={choosePlan} onClick={continueFromCta}>{jobLabel}</PlanChoiceButton>;
        $[103] = choosePlan;
        $[104] = continueAriaLabel;
        $[105] = continueFromCta;
        $[106] = ctaProps;
        $[107] = guest;
        $[108] = jobLabel;
        $[109] = low;
        $[110] = priced;
        $[111] = product;
        $[112] = t34;
      } else {
        t34 = $[112];
      }
      let t35;
      if ($[113] !== t33 || $[114] !== t34) {
        t35 = <HStack gap={3} justify="space-between" w="full" wrap="wrap">{t33}{t34}</HStack>;
        $[113] = t33;
        $[114] = t34;
        $[115] = t35;
      } else {
        t35 = $[115];
      }
      return t35;
    }
  }
  const t23 = guest ? "colorPalette.container" : "colorPalette.card/50";
  const t24 = guest ? "colorPalette.border" : "border.emphasized";
  const t25 = guest ? undefined : "orange.solid";
  const t26 = guest ? "1px" : "3px 1px 1px";
  const t27 = density === "overlay" ? "lg" : undefined;
  const t28 = density === "page" || density === "overlay" ? "40rem" : "full";
  let t29;
  if ($[116] !== density) {
    t29 = density === "overlay" ? {
      base: 5,
      md: 8
    } : {
      base: 5,
      md: 6
    };
    $[116] = density;
    $[117] = t29;
  } else {
    t29 = $[117];
  }
  let t30;
  if ($[118] !== guest || $[119] !== promotion) {
    t30 = promotion && !guest ? <Badge alignSelf="flex-start" colorPalette="green" variant="surface">{promotion.label}</Badge> : null;
    $[118] = guest;
    $[119] = promotion;
    $[120] = t30;
  } else {
    t30 = $[120];
  }
  let t31;
  if ($[121] !== density) {
    t31 = density === "page" || density === "overlay" ? {
      base: "xl",
      md: "2xl"
    } : "lg";
    $[121] = density;
    $[122] = t31;
  } else {
    t31 = $[122];
  }
  let t32;
  if ($[123] !== headlineText || $[124] !== t31) {
    t32 = <Heading size={t31} textWrap="balance">{headlineText}</Heading>;
    $[123] = headlineText;
    $[124] = t31;
    $[125] = t32;
  } else {
    t32 = $[125];
  }
  const t33 = guestNextStep ?? resolved.supporting;
  let t34;
  if ($[126] !== t33) {
    t34 = <Text color="fg.muted" textStyle="sm">{t33}</Text>;
    $[126] = t33;
    $[127] = t34;
  } else {
    t34 = $[127];
  }
  let t35;
  if ($[128] !== t30 || $[129] !== t32 || $[130] !== t34) {
    t35 = <Stack gap={2}>{t30}{t32}{t34}</Stack>;
    $[128] = t30;
    $[129] = t32;
    $[130] = t34;
    $[131] = t35;
  } else {
    t35 = $[131];
  }
  let t36;
  if ($[132] !== intent || $[133] !== product || $[134] !== showFeatures) {
    t36 = showFeatures && product ? <Stack gap={4}><Separator /><PaywallFeatureList intent={intent} limit={GATE_FEATURE_LIMIT} product={product} /></Stack> : null;
    $[132] = intent;
    $[133] = product;
    $[134] = showFeatures;
    $[135] = t36;
  } else {
    t36 = $[135];
  }
  let t37;
  if ($[136] !== actions || $[137] !== t23 || $[138] !== t24 || $[139] !== t25 || $[140] !== t26 || $[141] !== t27 || $[142] !== t28 || $[143] !== t29 || $[144] !== t35 || $[145] !== t36) {
    t37 = <Stack bg={t23} borderColor={t24} borderRadius="lg" borderTopColor={t25} borderWidth={t26} boxShadow={t27} gap={5} maxW={t28} p={t29} w="full">{t35}{t36}{actions}</Stack>;
    $[136] = actions;
    $[137] = t23;
    $[138] = t24;
    $[139] = t25;
    $[140] = t26;
    $[141] = t27;
    $[142] = t28;
    $[143] = t29;
    $[144] = t35;
    $[145] = t36;
    $[146] = t37;
  } else {
    t37 = $[146];
  }
  const gateContent = t37;
  if (density !== "overlay") {
    return gateContent;
  }
  let t38;
  if ($[147] !== children) {
    t38 = children ? <Box aria-hidden={true} inert={true} filter="blur(1.5px)" gridArea="1 / 1" p={6} pointerEvents="none">{children}</Box> : null;
    $[147] = children;
    $[148] = t38;
  } else {
    t38 = $[148];
  }
  let t39;
  if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <Box bgGradient="to-b" gradientFrom="bg/40" gradientTo="bg" gradientVia="bg/90" gridArea="1 / 1" zIndex={1} />;
    $[149] = t39;
  } else {
    t39 = $[149];
  }
  let t40;
  if ($[150] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = {
      base: 4,
      md: 8
    };
    $[150] = t40;
  } else {
    t40 = $[150];
  }
  let t41;
  if ($[151] !== gateContent) {
    t41 = <Box alignSelf="end" gridArea="1 / 1" justifySelf="center" maxW="40rem" pb={t40} pt={24} px={4} w="full" zIndex={2}><Box bg="bg" borderRadius="lg">{gateContent}</Box></Box>;
    $[151] = gateContent;
    $[152] = t41;
  } else {
    t41 = $[152];
  }
  let t42;
  if ($[153] !== t38 || $[154] !== t41) {
    t42 = <Box aria-label="Locked content preview" display="grid" minH="30rem" overflow="hidden" w="full">{t38}{t39}{t41}</Box>;
    $[153] = t38;
    $[154] = t41;
    $[155] = t42;
  } else {
    t42 = $[155];
  }
  return t42;
};